import ApiService from '@/shared/services/api.service';
import JwtService from '@/shared/services/jwt.service';
import OrgService from '@/shared/services/org.service';
import MenuService from '@/shared/services/menu.service';
import Errors from '@/shared/error/errors';
import i18nService from '@/shared/services/i18n.service';
import { LANGUAGE_CHOICE_MODE } from '@/shared/i18n/language-choice';
import { routerAsync } from '@/app-module';
import { AtlasService } from '@/modules/atlas/atlas-service';

// action types
export const DO_INIT = 'doInit';
export const DO_WAIT_UNTIL_INIT = 'doWaitUntilInit';
export const LOGIN = 'login';
export const LOGOUT = 'logout';
export const REGISTER = 'register';
export const RESET_PASSWORD = 'resetPassword';
export const CHANGE_PASSWORD = 'changePassword';
export const SEND_PASSWORD_RESET_EMAIL = 'sendPasswordResetEmail';
export const SEND_CONFIRMATION_EMAIL = 'sendConfirmationEmail';
export const VERIFY_PHONE = 'verifyPhone';
export const VALIDATE_PHONE = 'validatePhone';
export const AUTH_FACEBOOK = 'authFacebook';
export const AUTH_GOOGLE = 'authGoogle';
export const AUTH_ROEID = 'authRoeId';
export const GET_GOOGLE_LOGIN_URL = 'getGoogleLoginUrl';
export const GET_PROFILE = 'getProfile';
export const UPDATE_PROFILE = 'updateProfile';
export const UPDATE_PROFILE_LANGUAGE = 'updateProfileLanguage';
export const REQUEST_LANGUAGE_SWITCH = 'requestLanguageSwitch';
export const CLEAR_LANGUAGE_CHOICE = 'clearLanguageChoice';
export const UPDATE_ACCOUNT_DETAILS = 'updateAccountDetails';
export const AUTH_VERIFICATION_TOKEN = 'authVerificationToken';
export const CHECK_VALIDATION_TOKEN = 'checkValidationToken';
export const ENROLL_STAFF = 'enrollStaff';
export const ENROLL_STAFF_REDIRECT = 'enrollStaffRedirect';
export const ENROLL_STAFF_STATUS = 'enrollStaffStatus';
export const UPDATE_STAFF_ENROLLED = 'updateStaffEnrolled';
export const LOGIN_DELEGATED_ORG = 'loginDelegatedOrg';
export const GET_ROEID_AUTH = 'getRoeIdAuth';
export const DEACTIVATE_ROEID = 'deactivateRoeId';
export const GET_LAST_PCUEL_SYNC = 'getLastPcuelSync';
export const GET_STS_AUTH = 'getStsAuth';
export const AUTH_STS = 'authSts';

// mutation types
export const PURGE_AUTH = 'logOut';
export const SET_AUTH = 'setUser';
export const SET_PROFILE = 'setProfile';
export const SET_ERROR = 'setError';
export const REGISTRATION_SUCCESS = 'registrationSuccess';
export const AUTH_SUCCESS = 'authSuccess';
export const SET_VERIFICATION_TOKEN = 'setVerificationToken';
export const SET_ACCOUNT_DETAILS = 'setAccountDetails';
export const SET_EXPIRED_TOKEN = 'setExpiredToken';
export const SET_VIDEO_ENROLLMENT_TOKEN = 'setVideoEnrollmentToken';
export const SET_PROFILE_ENROLLED = 'setProfileEnrolled';
export const SET_INITIAL_STATE = 'setInitialState';
export const SET_CURRENT_USER_ORG_ID = 'setCurrentUserOrgId';
export const SET_LAST_PCUEL_SYNC = 'setLastPcuelSync';
export const SET_LANGUAGE_CHOICE = 'setLanguageChoice';

import Roles from '@/security/roles';
import { storeAsync } from '@/app-module';
import { isObjectEmpty } from '@/core/helpers/globalMethods';
import { getPcuelAccountId, destroyPcuelAccountId, destroyPcuelOrganizationId, fetchLastPcuelSync } from '@/shared/services/pcuel-account.service';

// A refresh never asks a new question; it only repeats a log-in one left unanswered.
const pendingLoginLanguageChoice = (accountLanguage) => {
    const pending = i18nService.getStoredLanguageChoice();

    return pending?.mode === LANGUAGE_CHOICE_MODE.LOGIN ? i18nService.getLoginLanguageChoice(accountLanguage) : null;
};

const state = {
    loadingInit: true,
    verificationToken: null,
    authToken: null,
    errors: null,
    user: {},
    isAuthenticated: !!JwtService.getToken(),
    isEmailConfirmed: false,
    isPhoneConfirmed: false,
    isAuthSucceeded: false,
    accountDetails: {},
    isTokenExpired: false,
    videoEnrollmentToken: null,
    initialState: {},
    currentUserOrganisationId: null,
    lastPcuelSync: null,
    languageChoice: null
};

const getters = {
    loadingInit: (state) => !!state.loadingInit,
    currentUser: (state) => state.user,
    isAuthenticated: (state) => state.isAuthenticated,
    isEmailConfirmed: (state) => state.isEmailConfirmed,
    isPhoneConfirmed: (state) => state.isPhoneConfirmed,
    isAuthSucceeded: (state) => state.isAuthSucceeded,
    accountDetails: (state) => state.accountDetails,
    isTokenExpired: (state) => state.isTokenExpired,
    currentUserType: (state) => state.user?.user_type_name,
    verificationToken: (state) => state.verificationToken,
    videoEnrollmentToken: (state) => state.videoEnrollmentToken,
    isStaff: (state, getters) => Roles.staffRoles.includes(getters.currentUserType),
    isSuperAdmin: (state, getters) => getters.currentUserType === Roles.values['super_admin'],
    isSimpleStaff: (state, getters) => Roles.values.staff === getters.currentUserType || Roles.values.admin === getters.currentUserType,
    isAdminStaff: (state, getters) => getters.isSimpleStaff && state.accountDetails.is_organisation_admin,
    isLegalEntity: (state, getters) => Roles.values.legal_entity === getters.currentUserType,
    isCitizen: (state, getters) => Roles.values.citizen === getters.currentUserType,
    isCitizenOrLegal: (state, getters) => Roles.values.citizen === getters.currentUserType || Roles.values.legal_entity === getters.currentUserType,
    canViewTeamCitizenFeatures: (state, getters) => getters.isLegalEntity && state.user?.roles?.includes('administrator'),
    registrationUserType: (state) => state.user?.registration_user_type,
    currentUserOrganisationId: (state) => state.currentUserOrganisationId,
    lastPcuelSync: (state) => state.lastPcuelSync,
    languageChoice: (state) => state.languageChoice,
    isAskingLoginLanguage: (state) => state.languageChoice?.mode === LANGUAGE_CHOICE_MODE.LOGIN
};

const actions = {
    async [GET_ROEID_AUTH](context, code) {
        return new Promise((resolve, reject) => {
            ApiService.get('auth/pscid/login', { code })
                .then(({ data }) => {
                    // context.commit(SET_AUTH, data);
                    // const store = storeAsync(); 
                    // Get initial state on login with ROeID
                    // context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(store.state))});
                    resolve(data);
                })
                .catch(({ response }) => {
                    reject(response);
                });
        });
    },
    async [DO_INIT](context) {
        const currentUrl = window.location.pathname;
        const isAtlasPublicView = currentUrl.includes('atlas-public-view');
        const isVerificationRoute = /\/auth\/(confirm-email|verify-sms)\/?$/i.test(currentUrl);
        context.commit('shared/setAtlasPublicView', isAtlasPublicView, { root: true });
        try {
            const token = JwtService.getToken();
            if (token) {
                const store = storeAsync();
                const state = store.state;
                // Get initial state on refresh page
                context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(state))});

                const orgId = OrgService.geOrgId();
                context.commit(SET_CURRENT_USER_ORG_ID, orgId);
                const response = await ApiService.get('profiles/me');
                const userDetails = await ApiService.get('users/me');
                context.commit(SET_PROFILE, { ...response.data, roles: userDetails.data.roles, permissions: userDetails.data.permissions });
                context.commit(SET_ACCOUNT_DETAILS , userDetails.data);
                // On verification routes the stored JWT may belong to a previous user.
                if (!isVerificationRoute) {
                    context.commit(SET_LANGUAGE_CHOICE, pendingLoginLanguageChoice(response.data?.language));
                }
                context.commit(SET_AUTH, {
                    data: {
                        authToken: token,
                    },
                    isEmailConfirmed: true,
                    isPhoneConfirmed: true,
                });
                // Keep selected (if present) the current selected organisation after page refresh
                if (orgId) {
                    await ApiService.get('profiles/me');
                    context.dispatch('shared/getOrganisation', orgId, { root: true });
                }
            } else {
                context.commit(PURGE_AUTH);
            }
        } catch (error) {
            context.commit(PURGE_AUTH);
            if (context.getters.isSuperAdmin) {
                context.commit('shared/clearOrganisation', null, { root: true });
            }
        }
    },
    async [DO_WAIT_UNTIL_INIT](context) {
        if (!context.getters.loadingInit) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            const waitUntilInitInterval = setInterval(() => {
                if (!context.getters.loadingInit) {
                    clearInterval(waitUntilInitInterval);
                    resolve();
                }
            }, 0);
        });
    },
    [LOGIN](context, credentials) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/login', credentials)
                .then(async ({ data }) => {
                    const { isEmailConfirmed, isPhoneConfirmed } = data;
                    if (!isEmailConfirmed || !isPhoneConfirmed) {
                        context.commit(AUTH_SUCCESS, {
                            email: credentials.email,
                            ...data,
                        });
                        if (data.data && data.data.verificationToken) {
                            context.commit(SET_VERIFICATION_TOKEN, data.data.verificationToken);
                        }
                        resolve(data);
                    } else {
                        context.commit(SET_AUTH, data);
                        context.dispatch('setExpiredToken', false)
                        const store = storeAsync();
                        const recordsPerPage = store.state.shared.settings.RECORDS_PER_PAGE;
                        if (!recordsPerPage) {
                            await context.dispatch('shared/fetchSettings', null,  { root: true });
                        }
                        // Get initial state on login
                        context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(store.state))});
                        resolve(data);
                    }
                })
                .catch(({ response }) => {
                    reject(response.data);
                });
        });
    },
    [AUTH_FACEBOOK](context, accessToken) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/facebook', { accessToken })
                .then(({ data }) => {
                    const { isEmailConfirmed, isPhoneConfirmed } = data;
                    if (!isEmailConfirmed || !isPhoneConfirmed) {
                        context.commit(AUTH_SUCCESS, data);
                        if (data.data && data.data.verificationToken) {
                            context.commit(SET_VERIFICATION_TOKEN, data.data.verificationToken);
                        }
                        resolve(data);
                    } else {
                        context.commit(SET_AUTH, data);
                        const store = storeAsync();
                        // Get initial state on login with FB
                        context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(store.state))});
                        resolve(data);
                    }
                })
                .catch((error) => {
                    Errors.handle(error);
                });
        });
    },
    [GET_GOOGLE_LOGIN_URL](context) {
      return new Promise((resolve, reject) => {
          ApiService.get('auth/google/url')
              .then(({ data }) => {
                const { url } = data.data;
                    resolve(url);
              })
              .catch((error) => {
                  Errors.handle(error);
              });
      });
    },
    [AUTH_GOOGLE](context, token) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/google/', { token })
                .then(({ data }) => {
                    const { isEmailConfirmed, isPhoneConfirmed } = data;
                    if (!isEmailConfirmed || !isPhoneConfirmed) {
                        context.commit(AUTH_SUCCESS, data);
                        if (data.data && data.data.verificationToken) {
                            context.commit(SET_VERIFICATION_TOKEN, data.data.verificationToken);
                        }
                        resolve(data);
                    } else {
                        context.commit(SET_AUTH, data);
                        const store = storeAsync();
                        // Get initial state on login with Google
                        context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(store.state))});
                        resolve(data);
                    }
                })
                .catch(({ response }) => {
                    reject(response);
                });
        });
    },
    [AUTH_ROEID](context, params) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/pscid/process-callback', params)
                .then(({ data }) => {
                    const { isEmailConfirmed, isPhoneConfirmed } = data;
                    if (!isEmailConfirmed || !isPhoneConfirmed) {
                        context.commit(AUTH_SUCCESS, data);
                        if (data.data && data.data.verificationToken) {
                            context.commit(SET_VERIFICATION_TOKEN, data.data.verificationToken);
                        }
                        resolve(data);
                    } else {
                        context.commit(SET_AUTH, data);
                        const store = storeAsync();
                        // Get initial state on login with RoeId
                        context.commit(SET_INITIAL_STATE, {...JSON.parse(JSON.stringify(store.state))});
                        resolve(data);
                    }
                })
                .catch(({ response }) => {
                    reject(response);
                });
        });
    },
    async [GET_STS_AUTH](context, code) {
        return new Promise((resolve, reject) => {
            ApiService.get('auth/sts/login', )
                .then(({ data }) => {
                    sessionStorage.setItem('sts_code_verifier', data.codeVerifier);
                    resolve(data);
                })
                .catch(({ response }) => {
                    reject(response);
                });
        });
    },    
    [AUTH_STS](context, { params, codeVerifier }) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/sts/process-callback', { params: {...params}, codeVerifier })
                .then(({ data }) => {
                    resolve(data);
                })
                .catch(({ response }) => {
                    reject(response);
                });
        });
    },         
    [LOGOUT](context) {
        // Clear sessionStorage items related to STS and loading states
        if (sessionStorage && Object.keys(sessionStorage)?.length) {
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith('sts_') || key === 'clearLoadingOnReload') {
                    sessionStorage.removeItem(key);
                }
            });
        }

        const payload = {
            token: JwtService.getToken()
        };

        if (payload.token) {
            return new Promise((resolve) => {
                ApiService.post('token-invalidate-requests', payload).then(() => {
                    context.dispatch('setMenu', 'default', { root: true });
                    context.commit(PURGE_AUTH);
                    if (context.getters.isSuperAdmin) {
                        context.commit('shared/clearOrganisation', null, { root: true });
                    }
                    const initialState = context.state.initialState;
                    if (!isObjectEmpty(initialState)) {
                        const { auth , ...values} = context.state.initialState;
                        const store = storeAsync();
                        // Replace store with initial one, beside auth. Auth state is changed on logout so it must be the same
                        store.replaceState({...store.state,...values});
                    }

                    resolve();
                }) .catch((error) => {
                    context.commit(PURGE_AUTH);
                    const router = routerAsync();
                    router.push({ name: 'signin' });
                });
            });
        } else {
            context.commit(PURGE_AUTH);
        }
    },
    [REGISTER](context, credentials) {
        return new Promise((resolve) => {
            ApiService.post('auth/register', credentials)
                .then(({ data }) => {
                    context.commit(REGISTRATION_SUCCESS, {
                        email: credentials.email,
                        ...data,
                    });
                    if (data.data && data.data.verificationToken) {
                        context.commit(SET_VERIFICATION_TOKEN, data.data.verificationToken);
                    }
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                });
        });
    },
    [RESET_PASSWORD](context, payload) {
        context.commit('shared/activateLoading', 'auth/resetPassword', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.patch('auth/reset-password', payload)
                .then(({ data }) => {
                    context.commit(PURGE_AUTH);
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                })
                .finally(() => {
                    context.commit('shared/deactivateLoading', 'auth/resetPassword', { root: true });
                });
        });
    },
    [DEACTIVATE_ROEID](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.patch('auth/pscid/unlink', payload)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                    reject(error);
                });   
        });        
    },
    [CHANGE_PASSWORD](context, payload) {
        context.commit('shared/activateLoading', 'auth/changePassword', { root: true });
        return new Promise((resolve, reject) => {
            ApiService.post('auth/change-password', payload)
                .then(({ data }) => {
                    context.commit(PURGE_AUTH);
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                })
                .finally(() => {
                    context.commit('shared/deactivateLoading', 'auth/changePassword', { root: true });
                });
        });
    },
    [SEND_PASSWORD_RESET_EMAIL](context, payload) {
        return new Promise((resolve) => {
            const email = payload;
            ApiService.post('recover-password', email)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                });
        });
    },
    [SEND_CONFIRMATION_EMAIL](context, payload) {
        return new Promise((resolve) => {
            const email = payload;
            ApiService.query('auth/resend', { params: { email } })
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    Errors.handle(error);
                });
        });
    },
    [VERIFY_PHONE](context, payload) {
        return new Promise((resolve, reject) => {
            const { phone, token } = payload;
            const body = {
                phone,
                token
            };
            ApiService.post('auth/phone/update', body)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [VALIDATE_PHONE](context, payload) {
        return new Promise((resolve, reject) => {
            const { phone, smsCode, token } = payload;
            const body = {
                phone,
                smsCode,
                token
            };
            ApiService.post('auth/phone/validate', body)
                .then(({ data }) => {
                    context.commit(SET_AUTH, data);
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                    reject(error);
                });
        });
    },
    [UPDATE_PROFILE](context, payload) {
        return new Promise((resolve, reject) => {
            const currentUser = context.getters.currentUser;
            const accountId = getPcuelAccountId();
            const fullPayload = { ...currentUser, ...payload };
            if (accountId) {
                fullPayload.pcuel_account_id = accountId;
            }
            ApiService.put('profiles/me', fullPayload)
                .then(({ data }) => {
                    if (accountId) {
                        destroyPcuelAccountId();
                        destroyPcuelOrganizationId();
                    }
                    context.commit(SET_PROFILE, data);
                    resolve(data);
                })
                .catch((error) => {
                    Errors.handle(error);
                    reject(error);
                });
        });
    },
    [UPDATE_PROFILE_LANGUAGE](context, language) {
        return new Promise((resolve, reject) => {
            // The gateway denies with 403, which must not raise the session-expired dialog.
            ApiService.patch('profiles/me/language', { language }, { suppressExpiredTokenModal: true })
                .then(({ data }) => {
                    context.commit(SET_PROFILE, { ...data, language });
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [REQUEST_LANGUAGE_SWITCH](context, language) {
        context.commit(SET_LANGUAGE_CHOICE, i18nService.getSwitchLanguageChoice(language));
    },
    [CLEAR_LANGUAGE_CHOICE](context) {
        context.commit(SET_LANGUAGE_CHOICE, null);
    },
    [GET_PROFILE](context) {
        return new Promise((resolve, reject) => {
            ApiService.get('profiles/me')
                .then(async ({ data }) => {
                    const userDetails = await ApiService.get('users/me');
                    context.commit(SET_PROFILE, { ...data, roles: userDetails?.data?.roles, permissions: userDetails?.data?.permissions });
                    context.commit(SET_ACCOUNT_DETAILS , userDetails.data);
                    context.commit(SET_CURRENT_USER_ORG_ID, userDetails.data?.organisation_id);
                    context.commit(SET_LANGUAGE_CHOICE, i18nService.getLoginLanguageChoice(data?.language));
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [AUTH_VERIFICATION_TOKEN](context, token) {
        context.commit(SET_VERIFICATION_TOKEN, token);
    },
    [UPDATE_ACCOUNT_DETAILS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.patch('users', payload)
                .then(({ data }) => {
                    context.commit(SET_ACCOUNT_DETAILS, data);
                    resolve(data);
                })
                .catch(({ error }) => {
                    context.commit(SET_ERROR, error.data.errors);
                    reject(error);
                });
        });
    },
    [SET_EXPIRED_TOKEN](context, tokenState) {
        context.commit(SET_EXPIRED_TOKEN, tokenState);
    },
    [CHECK_VALIDATION_TOKEN](context, token) {
        return new Promise((resolve, reject) => {
            ApiService.query('auth/check-validation-token', { params: { token } })
                .then(() => {
                    resolve();
                })
                .catch(({ error }) => {
                    reject(error);
                });
        });
    },
    [ENROLL_STAFF](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post('video-enrollment/', payload)
                .then(({ data }) => {
                    context.commit(SET_VIDEO_ENROLLMENT_TOKEN, data)
                    resolve(data);
                })
                .catch(( error ) => {
                    Errors.handle(error);
                    reject();
                });
        });
    },
    [ENROLL_STAFF_REDIRECT](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.get(`video-enrollment/redirect/${payload.id}`)
                .then((response) => {
                    resolve(response);
                })
                .catch(({ error }) => {
                    reject(error);
                });
        });
    },
    [ENROLL_STAFF_STATUS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.get(`video-enrollment/status/${payload.id}`)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch(({ error }) => {
                    reject(error);
                });
        });
    },
    [UPDATE_STAFF_ENROLLED](context) {
        context.commit(SET_PROFILE_ENROLLED, true);
    },
    [LOGIN_DELEGATED_ORG](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post('auth/login-delegated-org', payload)
                .then(({ data }) => {
                    context.commit(SET_AUTH, {
                        data: {
                            authToken: data.data.token,
                        },
                        isEmailConfirmed: true,
                        isPhoneConfirmed: true,
                    });
                    context.commit(SET_CURRENT_USER_ORG_ID, payload.organisation_id);
                    OrgService.saveOrgId(payload.organisation_id);
                    resolve(payload.organisation_id);
                })
                .catch(({ error }) => {
                    reject(error);
                });
        });
    },
    [GET_LAST_PCUEL_SYNC](context, payload) {
        return new Promise((resolve, reject) => {
            fetchLastPcuelSync(payload)
                .then((data) => {
                    if (!data) return resolve();
                    context.commit(SET_LAST_PCUEL_SYNC, data);
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
};

const mutations = {
    [SET_ERROR](state, error) {
        state.errors = error;
        state.loadingInit = false;
    },
    [SET_AUTH](state, payload) {
        const { isEmailConfirmed, isPhoneConfirmed } = payload;
        const { authToken, verificationToken } = payload.data;
        if (verificationToken) {
            state.verificationToken = verificationToken;
        }
        state.isAuthSucceeded = true;
        state.isEmailConfirmed = isEmailConfirmed;
        state.isPhoneConfirmed = isPhoneConfirmed;
        state.errors = {};
        if (authToken) {
            state.authToken = authToken;
            state.isAuthenticated = true;
            JwtService.saveToken(authToken);
        }
        state.loadingInit = false;
    },
    [PURGE_AUTH](state) {
        state.languageChoice = null;
        i18nService.storeLanguageChoice(null);
        state.isAuthenticated = false;
        state.isAuthSucceeded = false;
        state.isEmailConfirmed = false;
        state.isPhoneConfirmed = false;
        state.authToken = null;
        state.verificationToken = null;
        state.user = {};
        state.accountDetails = {};
        state.errors = {};
        JwtService.destroyToken();
        OrgService.destroyOrgId();
        MenuService.destroyMenuStatus();
        AtlasService.destroyPersistedParcel();
        state.loadingInit = false;
        state.isTokenExpired = false;
    },
    [SET_PROFILE](state, profile) {
        state.user = {
            ...state.user,
            ...profile
        };
    },
    [REGISTRATION_SUCCESS](state, payload) {
        state.user.email = payload.email;
        state.isEmailConfirmed = payload.isEmailConfirmed;
        state.isAuthSucceeded = true;
    },
    [AUTH_SUCCESS](state, payload) {
        state.user.email = payload.email;
        state.isEmailConfirmed = payload.isEmailConfirmed;
        state.isPhoneConfirmed = payload.isPhoneConfirmed;
        state.isAuthSucceeded = true;
    },
    [SET_VERIFICATION_TOKEN](state, token) {
        state.verificationToken = token;
    },
    [SET_ACCOUNT_DETAILS](state, details) {
        state.accountDetails = {
            ...state.accountDetails,
            ...details
        };
    },
    [SET_EXPIRED_TOKEN](state, tokenState) {
        state.isTokenExpired = tokenState;
    },
    [SET_VIDEO_ENROLLMENT_TOKEN](state, payload) {
        state.videoEnrollmentToken = payload.context;
    },
    [SET_PROFILE_ENROLLED](state, enrolled) {
        state.user.enrolled = enrolled;
    },
    [SET_INITIAL_STATE](state, obj) {
        state.initialState = obj;
    },
    [SET_CURRENT_USER_ORG_ID](state, data) {
        state.currentUserOrganisationId = data;
    },
    [SET_LAST_PCUEL_SYNC](state, data) {
        state.lastPcuelSync = data;
    },
    [SET_LANGUAGE_CHOICE](state, choice) {
        state.languageChoice = choice;
        i18nService.storeLanguageChoice(choice);
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
